import { useEffect } from "react"

import { useQuery } from "@tanstack/react-query"

import { supabase } from "../lib/supabase"
import { DateFilter } from "../types/filters"

export interface VisitorFunnelMetrics {
  totalVisitors: number
  startedForm: number
  completedForm: number
  startedFormPercentage: number
  completedFormPercentage: number
  completedOfStartedPercentage: number
}

/**
 * Hook to fetch visitor funnel metrics data from the metrics-visitor-funnel endpoint
 * @param dateFilter Optional date range filter
 * @returns Query result with visitor funnel metrics
 */
export const useVisitorFunnelData = (dateFilter?: DateFilter, jobId?: string) => {
  const query = useQuery<VisitorFunnelMetrics>({
    queryKey: ["visitorFunnel", dateFilter, jobId ?? "all"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("metrics-visitor-funnel", {
        body: { dateFilter, jobId: jobId ?? null },
      })

      if (error) {
        throw new Error(`Error fetching visitor funnel data: ${error.message}`)
      }

      return data
    },
    // Keep data fresh for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Enable refetch on window focus
    refetchOnWindowFocus: true,
  })

  // Effect to refetch data when component mounts/tab becomes active
  useEffect(() => {
    // Refetch data when component mounts
    query.refetch()

    // Set up event listener for visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        query.refetch()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    // Clean up event listener
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [query])

  return query
}
